import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CardContent, Checkbox, FormControlLabel, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { IconTools, IconDeviceFloppy, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const ServiceForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [appId, setAppId] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [categories, setCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});

    const [form, setForm] = useState({
        name: '',
        category: '',
        code: '',
        price: '',
        unit_of_measure: '',
        tax_code: '',
        iss_rate: '',
        is_active: true
    });

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadClassificationData();
        if (isEdit) loadService();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadClassificationData = async () => {
        const [categoriesRes, unitsRes, fieldsRes] = await Promise.all([
            api.get(`v1/apps/${appId}/service-categories/`),
            api.get(`v1/apps/${appId}/units-of-measure/`),
            api.get(`v1/apps/${appId}/service_fields/`)
        ]);
        setCategories(categoriesRes.data || []);
        setUnits(unitsRes.data || []);
        setFieldsConfig(fieldsRes.data || []);
    };

    const loadService = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/services/${id}/`);
            const service = res.data;
            setForm({
                name: service.name || '',
                category: service.category || '',
                code: service.code || '',
                price: service.price ?? '',
                unit_of_measure: service.unit_of_measure || '',
                tax_code: service.tax_code || '',
                iss_rate: service.iss_rate ?? '',
                is_active: service.is_active
            });
            setFields(service.custom_fields || {});
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar serviço' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => setForm({ ...form, [field]: value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name) {
            setStatus({ type: 'error', message: 'Informe o nome do serviço' });
            return;
        }

        setStatus({ type: '', message: '' });
        setSaving(true);

        const payload = { ...form, fields };

        try {
            if (isEdit) {
                await api.patch(`v1/apps/${appId}/services/${id}/`, payload);
                setStatus({ type: 'success', message: 'Serviço atualizado com sucesso!' });
                setSaving(false);
            } else {
                await api.post(`v1/apps/${appId}/services/`, payload);
                // navega embora sem tocar em `setSaving`/`setStatus` depois -
                // o componente já vai estar desmontado quando essa promise
                // resolver, e atualizar estado de um componente desmontado
                // gera o warning "Can't perform a React state update..."
                history.push('/dashboard/config/services');
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar serviço' });
            setSaving(false);
        }
    };

    const deleteService = async () => {
        if (!window.confirm(`Excluir o serviço "${form.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/services/${id}/`);
            history.push('/dashboard/config/services');
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir serviço' });
        }
    };

    if (loading) return null;

    return (
        <Box>
            <PageHeader
                icon={IconTools}
                gradient="teal"
                title={isEdit ? 'Editar Serviço' : 'Novo Serviço'}
                subtitle="Cadastro de serviços do catálogo"
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nome"
                                    fullWidth
                                    required
                                    value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Categoria"
                                    fullWidth
                                    value={form.category}
                                    onChange={(e) => handleChange('category', e.target.value)}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {categories.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Código"
                                    fullWidth
                                    value={form.code}
                                    onChange={(e) => handleChange('code', e.target.value)}
                                    helperText="Gerado automaticamente se deixado em branco"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Preço"
                                    fullWidth
                                    value={form.price}
                                    onChange={(e) => handleChange('price', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    label="Unidade de Medida"
                                    fullWidth
                                    value={form.unit_of_measure}
                                    onChange={(e) => handleChange('unit_of_measure', e.target.value)}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {units.map((u) => (
                                        <MenuItem key={u.id} value={u.id}>
                                            {u.code} - {u.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Código de tributação (LC 116)"
                                    fullWidth
                                    value={form.tax_code}
                                    onChange={(e) => handleChange('tax_code', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Alíquota de ISS (%)"
                                    fullWidth
                                    value={form.iss_rate}
                                    onChange={(e) => handleChange('iss_rate', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={!!form.is_active} onChange={(e) => handleChange('is_active', e.target.checked)} />
                                    }
                                    label="Serviço ativo"
                                />
                            </Grid>

                            {fieldsConfig.length > 0 && (
                                <Grid item xs={12}>
                                    <Typography fontWeight={700} mb={1}>
                                        Campos adicionais
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {fieldsConfig.map((f) => (
                                            <Grid item xs={12} sm={6} key={f.id}>
                                                <TextField
                                                    label={f.title}
                                                    required={f.required}
                                                    fullWidth
                                                    value={fields[f.name] || ''}
                                                    onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                    {isEdit && (
                                        <Button color="error" startIcon={<IconTrash size={18} />} onClick={deleteService}>
                                            Excluir
                                        </Button>
                                    )}
                                    <Button onClick={() => history.push('/dashboard/config/services')}>Voltar</Button>
                                    <Button type="submit" variant="contained" startIcon={<IconDeviceFloppy size={18} />} disabled={saving}>
                                        {saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar Serviço'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default ServiceForm;
